import { useEffect, useRef, useState, forwardRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { pressLogos } from "@/data/social-proof";
import { getIntroPageContent } from "@/lib/intro.functions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function useIntroContent(experienceSlug?: string | null) {
  const fetchContent = useServerFn(getIntroPageContent);
  return useQuery({
    queryKey: ["intro-content", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchContent({ data: { experience_slug: experienceSlug } })
        : fetchContent(),
  });
}

export function FeaturedInMarquee({
  experienceSlug,
  overlay = false,
}: {
  experienceSlug?: string | null;
  overlay?: boolean;
}) {
  const { data } = useIntroContent(experienceSlug);
  const label = data?.featured_label || "FEATURED IN";
  const items = [...pressLogos, ...pressLogos];
  return (
    <section className={`relative w-full py-6 ${overlay ? "" : "bg-background"}`}>
      <div className="max-w-4xl mx-auto overflow-hidden">
        <div className="flex items-center gap-3 max-w-xs mx-auto mb-6 px-6">
          <div className={`h-px flex-1 ${overlay ? "bg-white/25" : "bg-foreground/10"}`} />
          <span
            className={`text-[9px] uppercase tracking-[0.28em] whitespace-nowrap ${
              overlay ? "text-white/80" : "text-foreground/40"
            }`}
          >
            {label}
          </span>
          <div className={`h-px flex-1 ${overlay ? "bg-white/25" : "bg-foreground/10"}`} />
        </div>
        <div className="relative">
          {!overlay && (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10" />
            </>
          )}
          <div className="flex w-max items-center animate-marquee gap-10 md:gap-12 px-6">
            {items.map((p, i) => (
              <img
                key={`${p.name}-${i}`}
                src={p.logo}
                alt={p.name}
                loading="lazy"
                decoding="async"
                height={32}
                width={120}
                className={`h-7 md:h-8 w-auto object-contain shrink-0 transition-opacity ${
                  overlay ? "opacity-90 hover:opacity-100" : "opacity-70 hover:opacity-100"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


type Pillar = {
  num: string;
  title: string;
  listLabel: string;
  body: string;
  img: string;
};

export function IntroStatement({ experienceSlug }: { experienceSlug?: string | null }) {
  const { data } = useIntroContent(experienceSlug);
  const headline =
    data?.intro_headline ||
    "Perfect for couples who want a truly unique romantic getaway";
  const body =
    data?.intro_body ||
    "Most hotels, resorts, and glamping sites make you compromise: road noise, light pollution, crowds, packed-in rooms, thin canvas, or a regular hotel-in-town experience.";

  // Only render pillar cards once the DB-driven content has loaded — never
  // fall back to AI-generated placeholder images.
  const pillars: Pillar[] = (data?.pillars ?? []).map((p, i) => ({
    num: p.num || String(i + 1).padStart(2, "0"),
    title: p.title,
    listLabel: p.title,
    body: p.body,
    img: p.image_url || "",
  }));

  const pillarsHeading =
    data?.pillars_heading || "How we built Backland to be different";
  const closerIntro =
    data?.closer_intro_text || "There are many places to stay near the Grand Canyon…";
  const closerText =
    data?.closer_text || "but there is nowhere else like Backland.";
  const loungeImage = data?.lounge_image_url || "";

  return (
    <>
      {/* Intro block — normal scroll */}
      <section className="relative w-full bg-background px-6 pt-10 pb-8 md:pt-14 md:pb-10">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-foreground text-3xl md:text-5xl leading-[1.15] tracking-tight">
            {headline}
          </h2>
          <p className="mt-6 md:mt-8 text-foreground/70 text-base md:text-lg leading-relaxed">
            {body}
          </p>
        </div>
      </section>

      {/* Sticky-heading card scroll */}
      <StickyHeadingSection
        pillars={pillars}
        heading={pillarsHeading}
        closerIntro={closerIntro}
        closerText={closerText}
        loungeImage={loungeImage}
      />
    </>
  );
}


type StickyHeadingSectionProps = {
  pillars: Pillar[];
  heading: string;
  closerIntro: string;
  closerText: string;
  loungeImage: string;
};

function StickyHeadingSection({
  pillars,
  heading,
  closerIntro,
  closerText,
  loungeImage,
}: StickyHeadingSectionProps) {
  const [revealed, setRevealed] = useState<boolean[]>(() => pillars.map(() => false));
  const [fadeAmount, setFadeAmount] = useState(0);
  const [headingHeight, setHeadingHeight] = useState(0);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const introLineRef = useRef<HTMLParagraphElement | null>(null);
  const headingRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      // --- READ PHASE: gather all layout measurements first ---
      const headingEl = headingRef.current;
      const headingRect = headingEl?.getBoundingClientRect();
      const headingBottom = headingRect?.bottom ?? 64 + 80;
      const nextHeadingHeight = headingRect?.height ?? null;

      const cardBottoms = cardRefs.current.map((el) =>
        el ? el.getBoundingClientRect().bottom : null,
      );

      const introEl = introLineRef.current;
      const introTop = introEl ? introEl.getBoundingClientRect().top : null;
      const viewportH = window.innerHeight;

      // --- WRITE PHASE: state updates only, no DOM reads after this point ---
      if (nextHeadingHeight !== null) {
        setHeadingHeight((h) => (h === nextHeadingHeight ? h : nextHeadingHeight));
      }

      const next = cardBottoms.map((b) => (b == null ? false : b < headingBottom));
      setRevealed((prev) =>
        prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next,
      );

      if (introTop !== null) {
        const distance = viewportH * 0.4;
        const progress = (headingBottom - introTop) / distance;
        setFadeAmount(Math.max(0, Math.min(1, progress)));
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    // Find scrollable ancestor (page may scroll inside a div rather than window)
    const scrollers: (Window | HTMLElement)[] = [window];
    let node: HTMLElement | null = headingRef.current;
    while (node && node !== document.body) {
      const cs = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(cs.overflowY)) scrollers.push(node);
      node = node.parentElement;
    }

    compute();
    scrollers.forEach((s) =>
      s.addEventListener("scroll", onScroll, { passive: true } as AddEventListenerOptions)
    );
    window.addEventListener("resize", onScroll);
    return () => {
      scrollers.forEach((s) => s.removeEventListener("scroll", onScroll));
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const stickyTop = "calc(env(safe-area-inset-top) + 4rem)";

  return (
    <section className="relative w-full bg-background">
      {/* Sticky scope: heading + cards + closer all release together at the end of this wrapper */}
      <div className="relative">
        <div
          ref={headingRef}
          className="sticky z-20 bg-background/85 backdrop-blur-md border-t border-foreground/10"
          style={{ top: stickyTop }}
        >
          <div className="max-w-2xl mx-auto px-6 py-4 md:py-5 text-center">
            <h3 className="font-serif font-bold text-gold text-xl md:text-3xl tracking-tight">
              {heading}
            </h3>
            <ol className="mt-2 md:mt-3 space-y-1 text-left max-w-md mx-auto">
              {pillars.map((p, i) => {
                const on = revealed[i];
                return (
                  <li
                    key={p.num}
                    aria-hidden={!on}
                    className="overflow-hidden transition-all duration-[350ms] ease-out"
                    style={{
                      opacity: on ? 1 : 0,
                      transform: on ? "translateY(0)" : "translateY(6px)",
                      maxHeight: on ? "3rem" : "0",
                    }}
                  >
                    <div className="flex items-baseline gap-2 text-foreground/85 text-lg md:text-xl">
                      <span className="font-serif text-gold tabular-nums">
                        {Number(p.num)}.
                      </span>
                      <span className="font-serif">{p.listLabel}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="px-4 md:px-6 pt-6 pb-10 space-y-6">
          {pillars.map((p, i) => (
            <PillarCard
              key={p.num}
              pillar={p}
              index={i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
            />
          ))}

          <div className="max-w-xl mx-auto px-6 pt-24 pb-8 text-center">
            <p
              ref={introLineRef}
              className="font-serif text-foreground/70 text-xl md:text-2xl leading-relaxed transition-opacity"
              style={{ opacity: 1 - fadeAmount }}
            >
              {closerIntro}
            </p>
          </div>

          <div
            className="sticky z-10"
            style={{ top: `calc(${stickyTop} + ${headingHeight}px)` }}
          >
            <div className="max-w-xl mx-auto px-6 pt-2 pb-6 text-center">
              <p className="font-serif italic text-gold text-2xl md:text-4xl leading-tight tracking-tight">
                {closerText}
              </p>
            </div>
          </div>
          {/* runway so the sticky closer has range to actually stick to the bottom of the list */}
          <div aria-hidden="true" className="h-[10vh]" />
        </div>
      </div>

      {/* Image is outside the sticky scope so the heading + closer scroll away before it enters view */}
      {loungeImage ? (
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="rounded-2xl overflow-hidden aspect-square md:aspect-auto">
            <img
              src={loungeImage}
              alt="Backland lounge interior with leather seating, pendant lighting, and forest views"
              loading="lazy"
              className="w-full h-full md:h-auto object-cover"
            />
          </div>
        </div>
      ) : null}
      <div aria-hidden="true" className="h-8" />
    </section>
  );
}

/**
 * Rewrite a Supabase storage public URL to the on-the-fly image transform
 * endpoint with a width hint. Returns the input unchanged for any other host.
 */
function supabaseImg(url: string, width: number): string {
  if (!url) return url;
  const rendered = url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
  if (rendered === url) return url; // not a Supabase storage URL
  const sep = rendered.includes("?") ? "&" : "?";
  return `${rendered}${sep}width=${width}&quality=72&resize=cover`;
}

function pillarSrcSet(url: string): { src: string; srcSet?: string } {
  // Only Supabase-hosted images support runtime resizing.
  if (!url.includes("/storage/v1/object/public/")) return { src: url };
  return {
    src: supabaseImg(url, 720),
    srcSet: `${supabaseImg(url, 720)} 720w, ${supabaseImg(url, 1100)} 1100w`,
  };
}

type PillarCardProps = { pillar: Pillar; index: number };
const PillarCard = forwardRef<HTMLElement, PillarCardProps>(function PillarCard(
  { pillar, index },
  ref
) {
  const { src, srcSet } = pillarSrcSet(pillar.img);
  return (
    <article
      ref={ref}
      data-idx={index}
      className="relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden min-h-[80vh] md:min-h-[70vh] flex animate-fade-in bg-foreground/5"
    >
      {pillar.img ? (
        <img
          src={src}
          srcSet={srcSet}
          sizes="(min-width: 768px) 672px, 100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : null}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/55 to-transparent"
      />
      <div className="relative z-10 mt-auto p-6 md:p-10 w-full">
        <div className="font-serif text-gold text-5xl md:text-6xl leading-none mb-3 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
          {pillar.num}
        </div>
        <h4 className="font-serif text-white text-2xl md:text-3xl tracking-tight mb-3 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
          {pillar.title}
        </h4>
        <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-xl [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
          {pillar.body}
        </p>
      </div>
    </article>
  );
});

const REVIEWS_FALLBACK_RATING = "4.9";
const REVIEWS_FALLBACK_COUNT = "300+";
const REVIEWS_MAX_ATTEMPTS = 3;
const REVIEWS_WATCHDOG_MS = [6000, 9000, 12000];
const REVIEW_CONTENT_MARKERS = [
  "google",
  "expedia",
  "booking",
  "tripadvisor",
  "well-traveled",
  "unmatched experience",
  "must visit",
  "practically perfect",
];
const ELFSIGHT_SCRIPT_ID = "elfsight-platform-script";
const ELFSIGHT_APP_CLASS = "elfsight-app-a8399f4b-46dc-453e-80ec-31c48f1611a9";

function StaticReviewsRating() {
  return (
    <div className="flex flex-col items-center justify-center gap-2">
      <div className="text-gold text-3xl md:text-4xl tracking-[0.2em] [text-shadow:0_1px_2px_rgba(0,0,0,0.15)]">
        ★★★★★
      </div>
      <div className="font-serif text-foreground text-2xl md:text-3xl">
        {REVIEWS_FALLBACK_RATING} overall rating
      </div>
      <div className="text-foreground/65 text-sm md:text-base">
        from {REVIEWS_FALLBACK_COUNT} verified guest reviews
      </div>
    </div>
  );
}

function ReviewsModalBody() {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");
  const attemptRef = useRef(0);
  const watchdogRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    let observer: MutationObserver | null = null;

    const clearWatchdog = () => {
      if (watchdogRef.current != null) {
        window.clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    };

    const hasReviewContent = () => {
      const el = widgetRef.current;
      if (!el) return false;
      const text = (el.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (text.length < 80) return false;
      return REVIEW_CONTENT_MARKERS.some((m) => text.includes(m));
    };

    const boot = () => {
      try {
        window.dispatchEvent(new Event("elfsight:load"));
        const eapps = (window as any).eapps;
        eapps?.AppsManager?.parseDocument?.();
        eapps?.initWidgetsFromBuffer?.();
      } catch {}
    };

    const attempt = () => {
      if (cancelled) return;
      if (hasReviewContent()) {
        setStatus("ready");
        return;
      }
      if (attemptRef.current >= REVIEWS_MAX_ATTEMPTS) {
        setStatus("failed");
        return;
      }
      const n = attemptRef.current;
      attemptRef.current = n + 1;
      setStatus("loading");

      // Reuse cached script when available, otherwise inject once.
      let script = document.getElementById(ELFSIGHT_SCRIPT_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = ELFSIGHT_SCRIPT_ID;
        script.src = "https://elfsightcdn.com/platform.js";
        script.async = true;
        script.onload = boot;
        script.onerror = () => {
          clearWatchdog();
          // Allow re-injection on the next attempt
          script?.remove();
          attempt();
        };
        document.body.appendChild(script);
      } else {
        boot();
      }

      clearWatchdog();
      const ms = REVIEWS_WATCHDOG_MS[n] ?? 12000;
      watchdogRef.current = window.setTimeout(() => {
        if (cancelled) return;
        if (hasReviewContent()) setStatus("ready");
        else attempt();
      }, ms);
    };

    if (widgetRef.current && "MutationObserver" in window) {
      observer = new MutationObserver(() => {
        if (hasReviewContent()) {
          clearWatchdog();
          setStatus("ready");
          observer?.disconnect();
          observer = null;
        }
      });
      observer.observe(widgetRef.current, { childList: true, subtree: true });
    }

    attempt();

    return () => {
      cancelled = true;
      clearWatchdog();
      observer?.disconnect();
    };
  }, []);

  return (
    <div className="relative min-h-[360px] w-full max-w-full overflow-x-hidden [&_*]:max-w-full">
      <div ref={widgetRef} className={`${ELFSIGHT_APP_CLASS} w-full max-w-full`} />

      {status === "loading" ? (
        <div
          role="status"
          aria-live="polite"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70"
        >
          <div className="h-10 w-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
          <div className="text-sm text-foreground/70">Loading reviews…</div>
        </div>
      ) : null}

      {status === "failed" ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <StaticReviewsRating />
          <p className="text-sm text-foreground/70 max-w-sm">
            Reviews are temporarily unavailable. Please try again in a moment.
          </p>
        </div>
      ) : null}
    </div>
  );
}


export function ReviewsSection({ experienceSlug }: { experienceSlug?: string | null }) {
  const { data } = useIntroContent(experienceSlug);
  const kicker = data?.reviews_kicker || "ARIZONA'S TOP RATED\nNATURE RESORT";
  const subtitle = data?.reviews_subtitle || "Tap the rating to read what our guests are saying";
  const [open, setOpen] = useState(false);

  return (
    <section className="relative w-full bg-background px-6 py-20 md:py-24">
      <div className="max-w-4xl mx-auto text-center">
        <div className="text-xs md:text-sm tracking-[0.45em] uppercase text-gold mb-6 md:mb-8 whitespace-pre-line">
          {kicker}
        </div>
        <p className="font-serif italic text-foreground/65 text-base md:text-lg mb-10">
          {subtitle}
        </p>

        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-label={`Read guest reviews — ${REVIEWS_FALLBACK_RATING} stars from ${REVIEWS_FALLBACK_COUNT} verified reviews`}
          className="group inline-flex flex-col items-center justify-center gap-2 rounded-2xl px-6 py-4 transition hover:bg-foreground/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
        >
          <StaticReviewsRating />
          <span className="mt-2 text-xs uppercase tracking-[0.3em] text-gold/80 group-hover:text-gold">
            Read reviews
          </span>
        </button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{"\n"}</DialogTitle>
              <DialogDescription>{"\n"}</DialogDescription>
            </DialogHeader>
            {open ? <ReviewsModalBody /> : null}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}



